<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :no-results-text="noResultsText"
    :external-search="true"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />
</template>

<script>
import { mapState } from "pinia";
import { useOrderInvoiceStore } from "@/stores/orderInvoiceStore";
import orderInvoicesApi from "@/services/api/orderInvoicesApi";
import { getCompanyId } from "@/utils/userUtils";

export default {
  name: "InvoiceSelect",

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    orderId: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите счёт",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    noResultsText: {
      type: String,
      default: "Счета не найдены",
    },
    autoSelectFirst: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "change"],

  data() {
    return {
      companyId: getCompanyId(),
      preselectedOption: null,
      isSearchActive: false,
      isFirstLoad: true,
    };
  },

  computed: {
    ...mapState(useOrderInvoiceStore, [
      "invoices",
      "isLoading",
      "pagination",
    ]),

    formattedOptions() {
      const options = this.invoices.map((invoice) => ({
        value: invoice.id,
        label: `Счёт #${invoice.number || invoice.id}`,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    orderId: {
      handler(newOrderId) {
        if (newOrderId) {
          this.isFirstLoad = true;
          this.loadInvoices();
        }
      },
      immediate: true,
    },

    formattedOptions: {
      handler(newOptions) {
        if (
          this.autoSelectFirst &&
          this.isFirstLoad &&
          newOptions.length > 0 &&
          !this.modelValue
        ) {
          this.$nextTick(() => {
            const firstValue = newOptions[0].value;
            this.$emit("update:modelValue", firstValue);
            this.$emit("change", firstValue);
            this.isFirstLoad = false;
          });
        }
      },
    },

    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }
      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }
      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useOrderInvoiceStore();
      await store.fetchInvoices(this.orderId, {
        search: searchData.query,
      });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useOrderInvoiceStore();
      await store.fetchInvoices(this.orderId);
    },

    async handleScrollEnd() {
      const store = useOrderInvoiceStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.isLoading
      ) {
        await store.loadMoreInvoices(this.orderId);
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useOrderInvoiceStore();
      const exists = store.invoices.find(
        (i) => String(i.id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        const response = await orderInvoicesApi.show(
          this.companyId,
          this.orderId,
          this.modelValue
        );
        const invoice = response.data.result || response.data;
        if (invoice) {
          this.preselectedOption = {
            value: invoice.id,
            label: `Счёт #${invoice.number || invoice.id}`,
          };
        }
      } catch (error) {
        console.error("Ошибка при загрузке выбранного счёта:", error);
      }
    },

    async loadInvoices() {
      if (!this.orderId) return;
      const store = useOrderInvoiceStore();
      await store.fetchInvoices(this.orderId);
      await this.fetchPreselectedOption();
    },
  },
};
</script>
